import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';
import { ENUM_AdvisorUserPermissionLevel } from '@api-new/common';
import { HTTP_AP_AdvisorUserPermissionSettings } from '@api-new/userservice';
import { RoutePathsEnum } from '@app/app.utils';
import { Store } from '@ngrx/store';
import { getNestedValue } from '@shared-lib/utils/get-nested-value';
import { PermissionEnum } from '@shared/enums/permission.enum';
import { ENUM_AdvisorUserPermissionLevelRecord } from '@shared/records/enum-advisor-user-permission-level.record';
import { selectPlatformUserPermissions } from '@shared/store/platform/platform.selectors';

export interface PermissionDefinitionModel {
  permission: PermissionEnum;
  permissionLevel?: ENUM_AdvisorUserPermissionLevel;
}

export type HasCurrentUserRequiredPermissionsFunctionModel = (permissionDefinitions: PermissionDefinitionModel[]) => boolean;

@Injectable({
  providedIn: 'root',
})
export class PermissionService {
  protected readonly router = inject(Router);
  protected readonly store = inject(Store);

  userPermissions: HTTP_AP_AdvisorUserPermissionSettings;

  constructor() {
    this.store.select(selectPlatformUserPermissions).subscribe((userPermissions) => {
      this.userPermissions = userPermissions;
    });
  }

  canActivate(route: ActivatedRouteSnapshot): boolean {
    const routePermissions = route.data.permissions;

    if (this.hasCurrentUserRequiredPermissions(routePermissions)) {
      return true;
    }
    void this.router.navigate([RoutePathsEnum.Platform, RoutePathsEnum.Dashboard]);

    return false;
  }

  hasCurrentUserRequiredPermissions(permissions: PermissionDefinitionModel[], hasOneOfPermissions?: boolean): boolean {
    const hasRequiredPermissions = permissions?.map(({ permission, permissionLevel }) =>
      this.hasCurrentUserPermissionOnLevel(permission, permissionLevel),
    );
    if (hasOneOfPermissions) {
      if (hasRequiredPermissions?.includes(true)) {
        return true;
      }
    } else {
      if (!hasRequiredPermissions?.includes(false)) {
        return true;
      }
    }
    return false;
  }

  private hasCurrentUserPermissionOnLevel(permission: PermissionEnum, permissionLevel?: ENUM_AdvisorUserPermissionLevel): boolean {
    const adviserUserPermissionLevel = getNestedValue(this.userPermissions, permission);
    if (!adviserUserPermissionLevel) {
      return false;
    }
    if (permissionLevel === undefined) {
      return true;
    }
    return (
      ENUM_AdvisorUserPermissionLevelRecord[adviserUserPermissionLevel].value >=
      ENUM_AdvisorUserPermissionLevelRecord[permissionLevel].value
    );
  }
}

export const PermissionGuard: CanActivateFn = (next: ActivatedRouteSnapshot): boolean => {
  return inject(PermissionService).canActivate(next);
};
